// frontend/src/modules/platform-billing/pages/BillingPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CreditCard, Download, SlidersHorizontal, ShieldOff } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { useApi } from '../../../design-system/useApi';
import { formatDate, formatDateTime } from '../../../design-system/format';
import { PILL_MOUNT } from '../../../design-system/motion';
import { cn } from '../../../lib/utils';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { billingApi } from '../api';
import { isCompActive } from '../types';
import type { TenantSubscription, PlatformInvoice, BillProjection, ModulePrice } from '../types';
import { FALLBACK_INCLUDED_SEATS } from '../../../marketing/pricingMath';
import { ManagePlanDialog, moduleLabel, PLAN_EDITABLE_STATUSES } from '../ManagePlanDialog';

export function BillingPage() {
  const subQ = useApi<{ subscription: TenantSubscription }>('/api/billing/subscription');
  const invQ = useApi<{ invoices: PlatformInvoice[] }>('/api/billing/invoices');
  const preQ = useApi<{ projection: BillProjection }>('/api/billing/preview');
  // The seat-definitions footnote quotes Payroll's included allowance. Read it
  // from the live catalogue rather than hardcoding "10", so the copy cannot
  // silently lie after a super-admin edits the band.
  const priceQ = useApi<{ prices: ModulePrice[] }>('/api/billing/module-prices');
  const [busyInvoiceId, setBusyInvoiceId] = React.useState<string | null>(null);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [savingCard, setSavingCard] = React.useState(false);
  const [managingPlan, setManagingPlan] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const confirm = useConfirm();

  async function pay(invoiceId: string) {
    setBusyInvoiceId(invoiceId);
    try {
      const r = await billingApi.payLink(invoiceId);
      window.location.assign(r.url);
    } catch (err: any) {
      toast.error('Could not generate pay link', { description: err.message });
    } finally { setBusyInvoiceId(null); }
  }

  async function addPaymentMethod() {
    setSavingCard(true);
    try {
      const r = await billingApi.addPaymentMethod();
      window.location.href = r.url;
    } catch (err: any) {
      toast.error('Could not start card setup', { description: err.message });
      setSavingCard(false);
    }
  }

  async function downloadPdf(inv: PlatformInvoice) {
    setDownloadingId(inv.id);
    try {
      await billingApi.downloadInvoicePdf(inv.id, inv.invoice_number);
    } catch (err: any) {
      toast.error('Could not download invoice PDF', { description: err.message });
    } finally { setDownloadingId(null); }
  }

  async function cancelSubscription(accessEndsOn: string | null) {
    const ok = await confirm({
      title: 'Cancel your Worqly subscription?',
      description: (
        <span>
          {accessEndsOn
            ? `You keep full access until ${formatDate(accessEndsOn)} — the end of the period you have already paid for. After that, modules stop working until you subscribe again.`
            : 'Your modules stop working once the current period ends.'}
          {' '}We won't invoice you again and the card on file is removed immediately.
          Your workspace and all your data stay exactly where they are — nothing is deleted.
        </span>
      ),
      confirmLabel: 'Cancel subscription',
      cancelLabel: 'Keep my subscription',
      destructive: true,
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await billingApi.cancelMySubscription();
      toast.success('Subscription cancelled', {
        description: accessEndsOn
          ? `You have access until ${formatDate(accessEndsOn)}. Your data is untouched.`
          : 'Your data is untouched.',
      });
      subQ.reload();
      preQ.reload();
    } catch (err: any) {
      toast.error('Could not cancel your subscription', { description: err.message });
    } finally { setCancelling(false); }
  }

  const sub = subQ.data?.subscription;
  const invoices = invQ.data?.invoices ?? [];
  const projection = preQ.data?.projection;
  const comped = isCompActive(sub);
  // A tenant can self-serve edit modules whenever the backend permits it
  // (trial / active / read_only). Comped accounts are never billed, so the plan
  // editor is hidden for them.
  const canManagePlan = Boolean(sub && !comped && PLAN_EDITABLE_STATUSES.includes(sub.status));

  return (
    <div>
      <PageHeader
        eyebrow="Money / Subscription"
        title="Subscription & billing"
        description="Your Worqly plan, upcoming cycle, paid invoices, and pay links."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-5xl space-y-6">
        {/* Honest error surface — previously a failed subscription load (e.g.
            a 403 for members without billing access) rendered a blank page. */}
        {subQ.status === 403 ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Billing is limited to members with billing access. Ask a workspace owner if you need to see the subscription." />
          </section>
        ) : subQ.error ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ErrorState title="Couldn't load your subscription" description={subQ.error} onRetry={subQ.reload} />
          </section>
        ) : (
        <>
        {/* Status banner */}
        {sub && (
          <motion.div variants={PILL_MOUNT} initial="initial" animate="animate">
            {comped ? <CompedBanner sub={sub} /> : <StatusBanner sub={sub} />}
          </motion.div>
        )}

        {/* Payment method — a comped account is never billed, so no card is needed. */}
        {sub && !comped && (
          <PaymentMethodCard sub={sub} saving={savingCard} onAdd={addPaymentMethod} />
        )}

        {/* Manage plan / modules — self-serve module changes for active subscribers. */}
        {sub && canManagePlan && (
          <ManagePlanCard sub={sub} onManage={() => setManagingPlan(true)} />
        )}

        {/* How your bill is calculated — the seat-vocabulary footnote (pricing revamp, 2026-07-25). */}
        {sub && (
          <SeatDefinitionsNote
            payrollIncluded={
              priceQ.data?.prices?.find((p) => p.module_key === 'payroll')?.included_seats
              ?? FALLBACK_INCLUDED_SEATS.payroll
            }
          />
        )}

        {/* Current cycle preview */}
        {projection && (
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className={VISUAL.dashSection}>Current cycle</h3>
              <p className="text-[12px] text-muted-foreground">
                {formatDate(projection.period_start)} → {formatDate(projection.period_end)}
              </p>
            </div>
            <ul className="mt-5 divide-y divide-border/40">
              {projection.lines.map((l) => (
                <li key={l.module_key} className="flex justify-between items-baseline py-3 text-[14px]">
                  <span className="text-foreground">{l.description}</span>
                  <span className={cn('font-mono tabular-nums', comped ? 'text-muted-foreground line-through' : 'text-foreground/85')}>{l.amount.toFixed(2)} {projection.currency}</span>
                </li>
              ))}
            </ul>
            {projection.lines.length === 0 && (
              <p className="text-[13px] text-muted-foreground italic py-3">No priced modules enabled yet.</p>
            )}
            <div className="flex items-baseline justify-between mt-3 pt-4 border-t border-border/40">
              <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground/80">Total {comped ? '(billed)' : '(projected)'}</span>
              {comped ? (
                <span className="inline-flex items-baseline gap-2">
                  <span className="text-[13px] text-muted-foreground line-through tabular-nums">{projection.total.toFixed(2)} {projection.currency}</span>
                  <span className="font-sans font-semibold text-[20px] tracking-[-0.02em] text-success">Free</span>
                </span>
              ) : (
                <span className="font-sans font-semibold text-[20px] tabular-nums tracking-[-0.02em] text-foreground">{projection.total.toFixed(2)} {projection.currency}</span>
              )}
            </div>
          </section>
        )}

        {/* Invoices */}
        <section>
          <h3 className={[VISUAL.dashSection, 'mb-3'].join(' ')}>Invoices</h3>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<PlatformInvoice>
              loading={invQ.loading}
              rows={invoices}
              rowKey={(r) => r.id}
              columns={[
                { key: 'number', header: 'Invoice #', cell: (r) => <span className="font-mono text-[12.5px]">{r.invoice_number}</span> },
                { key: 'period', header: 'Period',    cell: (r) => `${formatDate(r.period_start)} → ${formatDate(r.period_end)}` },
                { key: 'total',  header: 'Total',     cell: (r) => <span className="font-mono tabular-nums font-semibold">{r.total} {r.currency}</span> },
                { key: 'status', header: 'Status',    cell: (r) => invoiceStatusBadge(r.status) },
                { key: 'issued', header: 'Issued',    cell: (r) => formatDateTime(r.issued_at), className: 'text-xs text-muted-foreground' },
                { key: 'due',    header: 'Due',       cell: (r) => formatDate(r.due_at), className: 'text-xs text-muted-foreground' },
                { key: 'actions', header: '', cell: (r) => (
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-9 px-3.5 text-[12.5px] font-semibold gap-1.5"
                      disabled={downloadingId === r.id}
                      onClick={() => downloadPdf(r)}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {downloadingId === r.id ? 'Preparing…' : 'PDF'}
                    </Button>
                    {r.status === 'open' && (
                      <Button size="sm" className="rounded-full h-9 px-4 text-[12.5px] font-semibold" disabled={busyInvoiceId === r.id} onClick={() => pay(r.id)}>
                        {busyInvoiceId === r.id ? 'Loading…' : 'Pay'}
                      </Button>
                    )}
                  </div>
                ) },
              ]}
              emptyState={
                invQ.error
                  ? <ErrorState title="Couldn't load invoices" description={invQ.error} onRetry={invQ.reload} />
                  : <EmptyState title="No invoices yet" description="Invoices are generated by the monthly cron after the trial ends." />
              }
            />
          </div>
        </section>

        {/* Self-serve cancellation — a subscription a customer cannot leave is
            a consumer-law problem, not just bad UX. Never deletes data. */}
        {sub && sub.status !== 'cancelled' && (
          <CancelSubscriptionCard
            sub={sub}
            busy={cancelling}
            onCancel={() => cancelSubscription(sub.current_period_end ?? null)}
          />
        )}
        </>
        )}
      </div>

      {sub && (
        <ManagePlanDialog
          open={managingPlan}
          onOpenChange={setManagingPlan}
          subscription={sub}
          onSaved={() => { subQ.reload(); preQ.reload(); }}
        />
      )}
    </div>
  );
}

function ManagePlanCard({ sub, onManage }: { sub: TenantSubscription; onManage: () => void }) {
  const keys = (sub.enabled_module_keys ?? []).filter((k) => k !== 'platform-billing');
  return (
    <section className={cn(VISUAL.bento, 'p-6 sm:p-7')}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <h3 className={VISUAL.dashSection}>Your plan</h3>
            <p className="text-[13.5px] text-muted-foreground mt-1 leading-relaxed">
              {keys.length > 0
                ? `${keys.length} module${keys.length === 1 ? '' : 's'} enabled. Turn modules on or off any time.`
                : 'No modules enabled yet. Choose the modules your team needs.'}
            </p>
            {keys.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {keys.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11.5px] font-medium text-foreground/80"
                  >
                    {moduleLabel(k)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full h-9 px-4 text-[12.5px] font-semibold shrink-0 gap-1.5"
          onClick={onManage}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Manage plan
        </Button>
      </div>
    </section>
  );
}

function PaymentMethodCard({ sub, saving, onAdd }: {
  sub: TenantSubscription;
  saving: boolean;
  onAdd: () => void;
}) {
  const hasCard = Boolean(sub.stripe_payment_method_id);
  const needsCard = sub.status === 'read_only' || sub.status === 'past_due';
  return (
    <section className={cn(
      VISUAL.bento, 'p-6 sm:p-7',
      !hasCard && needsCard && 'ring-2 ring-warning/50',
    )}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <h3 className={VISUAL.dashSection}>Payment method</h3>
            {hasCard ? (
              <p className="text-[13.5px] text-foreground mt-1">
                <span className="capitalize">{sub.payment_method_brand ?? 'Card'}</span>
                {sub.payment_method_last4 && (
                  <span className="font-mono tabular-nums"> •••• {sub.payment_method_last4}</span>
                )}
                <span className="text-muted-foreground"> — used automatically for unpaid invoices.</span>
              </p>
            ) : (
              <p className="text-[13.5px] text-muted-foreground mt-1 leading-relaxed">
                {needsCard
                  ? 'No card on file. Add one to reactivate your subscription and settle invoices automatically.'
                  : 'No card on file. Add one so future invoices are settled automatically.'}
              </p>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant={hasCard ? 'outline' : 'default'}
          className="rounded-full h-9 px-4 text-[12.5px] font-semibold shrink-0"
          disabled={saving}
          onClick={onAdd}
        >
          {saving ? 'Redirecting…' : hasCard ? 'Update card' : 'Add payment method'}
        </Button>
      </div>
    </section>
  );
}

/**
 * Self-serve cancellation. Deliberately plain-spoken: what stops, exactly when
 * it stops, what happens to the card, and what happens to the data. Sits at the
 * bottom of the page and is styled as a quiet destructive action — findable
 * without being a trap.
 */
function CancelSubscriptionCard({ sub, busy, onCancel }: {
  sub: TenantSubscription;
  busy: boolean;
  onCancel: () => void;
}) {
  const endsOn = sub.current_period_end ? formatDate(sub.current_period_end) : null;
  const accessLine =
    sub.status === 'trial'
      ? 'Your trial ends immediately and no card is ever charged.'
      : endsOn
      ? `You keep access until ${endsOn} — the end of the period you have already paid for.`
      : 'You keep access until the end of the period you have already paid for.';
  return (
    <section className={cn(VISUAL.bento, 'p-6 sm:p-7')}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-destructive/10 dark:bg-destructive/15 flex items-center justify-center shrink-0">
            <ShieldOff className="h-5 w-5 text-destructive dark:text-destructive" />
          </div>
          <div className="min-w-0">
            <h3 className={VISUAL.dashSection}>Cancel subscription</h3>
            <p className="text-[13.5px] text-muted-foreground mt-1 leading-relaxed max-w-prose">
              {accessLine} After that, your modules stop working until you subscribe again.
            </p>
            <ul className="mt-2.5 space-y-1 text-[13px] text-muted-foreground leading-relaxed">
              <li>· You are never invoiced again, and the card on file is removed straight away.</li>
              <li>· Your workspace, people, invoices and files are kept — cancelling deletes nothing.</li>
              <li>· Already-issued invoices stay payable and downloadable here.</li>
            </ul>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full h-9 px-4 text-[12.5px] font-semibold shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 dark:border-destructive/50 dark:text-destructive dark:hover:bg-destructive/20"
          disabled={busy}
          onClick={onCancel}
        >
          {busy ? 'Cancelling…' : 'Cancel subscription'}
        </Button>
      </div>
    </section>
  );
}

/**
 * "How your bill is calculated" footnote — spells out the three per-employee
 * seat definitions the billing engine metering uses (pricing revamp,
 * 2026-07-25). Always shown once a subscription has loaded, independent of
 * whether the current period preview has any metered lines.
 */
function SeatDefinitionsNote({ payrollIncluded }: { payrollIncluded: number }) {
  return (
    <section className={cn(VISUAL.bento, 'p-5 sm:p-6')}>
      <h3 className={VISUAL.dashSection}>How your bill is calculated</h3>
      <p className="mt-2 text-[13px] text-muted-foreground leading-relaxed">
        Every workspace pays a flat $9/mo core fee. Some modules add a flat monthly
        fee; others bill per employee from your workspace as it stands on your
        renewal date — billing is in advance, not metered after the fact:
      </p>
      <ul className="mt-3 space-y-1.5 text-[13px] text-muted-foreground leading-relaxed">
        <li><span className="font-semibold text-foreground">Clock-in employee</span> — any active employee not billed as monitored (when Monitoring is off, that's all active employees).</li>
        <li><span className="font-semibold text-foreground">Monitored employee</span> — an active employee running the desktop agent.</li>
        <li><span className="font-semibold text-foreground">Payroll</span> — a flat base fee covering your first {payrollIncluded} active {payrollIncluded === 1 ? 'employee' : 'employees'}, then a per-employee rate for anyone beyond that.</li>
      </ul>
    </section>
  );
}

function CompedBanner({ sub }: { sub: TenantSubscription }) {
  const body = sub.comp_expires_at
    ? `Your workspace is on a complimentary plan through ${formatDate(sub.comp_expires_at)}. You have full access and won't be invoiced during this period.`
    : 'Your workspace is on a complimentary plan. You have full access and are never invoiced.';
  return (
    <div className={cn('relative overflow-hidden rounded-2xl ring-1 ring-border/40 shadow-card', 'bg-success/8')}>
      <div className={cn('absolute inset-y-0 left-0 w-1', 'bg-success')} />
      <div className="px-6 py-5 pl-7">
        <div className="text-sm font-semibold text-success">Comped · Free account</div>
        <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function StatusBanner({ sub }: { sub: TenantSubscription }) {
  const tone: 'info' | 'success' | 'warning' | 'destructive' | 'muted' =
    sub.status === 'trial' ? 'info' :
    sub.status === 'active' ? 'success' :
    sub.status === 'past_due' ? 'warning' :
    sub.status === 'read_only' ? 'warning' :
    sub.status === 'suspended' ? 'destructive' :
    'muted';
  const colorMap = {
    info: { bg: 'bg-info/8', stripe: 'bg-info', text: 'text-info' },
    success: { bg: 'bg-success/8', stripe: 'bg-success', text: 'text-success' },
    warning: { bg: 'bg-warning/12', stripe: 'bg-warning', text: 'text-warning-foreground dark:text-warning' },
    destructive: { bg: 'bg-destructive/8', stripe: 'bg-destructive', text: 'text-destructive' },
    muted: { bg: 'bg-muted', stripe: 'bg-muted-foreground/40', text: 'text-foreground' },
  }[tone];

  const headline =
    sub.status === 'trial' ? 'Free trial' :
    sub.status === 'active' ? 'Subscription active' :
    sub.status === 'past_due' ? 'Payment past due' :
    sub.status === 'read_only' ? 'Trial ended — read-only mode' :
    sub.status === 'suspended' ? 'Subscription suspended' :
    'Subscription cancelled';

  const body =
    sub.status === 'trial'
      ? `Your trial ends on ${formatDate(sub.trial_ends_at!)}. After that you'll be billed monthly for your enabled modules.`
      : sub.status === 'active'
      ? `Current period ends ${formatDate(sub.current_period_end)}. Billing is in advance — your next invoice is issued at the start of the following period.`
      : sub.status === 'past_due'
      ? `You have an unpaid invoice. Settle it before ${sub.grace_period_ends_at ? formatDate(sub.grace_period_ends_at) : 'the grace period ends'} to avoid suspension.`
      : sub.status === 'read_only'
      ? 'Your trial ended without a payment method, so modules are read-only. Add a payment method below to reactivate your subscription.'
      : sub.status === 'suspended'
      ? 'Module access is paused. Pay any open invoice below to restore access.'
      : `Cancelled on ${sub.cancelled_at ? formatDateTime(sub.cancelled_at) : 'unknown date'}. Contact support to reactivate.`;

  return (
    <div className={cn('relative overflow-hidden rounded-2xl ring-1 ring-border/40 shadow-card', colorMap.bg)}>
      <div className={cn('absolute inset-y-0 left-0 w-1', colorMap.stripe)} />
      <div className="px-6 py-5 pl-7">
        <div className={cn('text-sm font-semibold', colorMap.text)}>{headline}</div>
        <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function invoiceStatusBadge(s: PlatformInvoice['status']): React.ReactNode {
  const tone: StatusTone =
    s === 'paid' ? 'success' :
    s === 'open' ? 'warning' :
    s === 'void' ? 'muted' :
    'destructive';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {s}
    </span>
  );
}
